import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import MarkdownContent from '../components/common/MarkdownContent';
import MiniChart from '../components/common/MiniChart';

const COMPANIES = ['Widget Financial', 'Frugal Prints'];
const FREQUENCIES = ['manual', 'hourly', 'daily', 'weekly', 'monthly'];

const PRESETS = [
  { label: 'Google Ads', query: 'Widget Financial Google Ads — Auto Refinance campaign, last 7 days: spend, clicks, conversions, CTR, cost/conv. Run the google-ads skill.' },
  { label: 'GA4 traffic', query: 'Widget Financial GA4 — last 30 days: sessions, active users, key events, top channel. Run the google-analytics skill.' },
  { label: 'SavvyMoney', query: 'Widget Financial SavvyMoney — latest enrolled, active, adoption %, and PQO CTR from the wf-savvy trend.' },
];

const trendColor = (t) =>
  t === 'up' ? 'text-emerald-600 dark:text-emerald-400'
  : t === 'down' ? 'text-red-500 dark:text-red-400'
  : 'text-gray-400';

const ago = (d) => {
  if (!d) return 'never';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Reports = () => {
  const { token } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // report object or {} for new
  const [viewing, setViewing] = useState(null); // report being viewed in detail
  const [busyId, setBusyId] = useState(null);

  const headers = { headers: { 'x-auth-token': token } };

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/reports', { headers: { 'x-auth-token': token } });
      setReports(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const runNow = async (id) => {
    setBusyId(id);
    try {
      const res = await axios.post(`/api/reports/${id}/run-now`, {}, headers);
      setReports((cur) => cur.map((r) => (r._id === id ? res.data : r)));
    } catch (e) {
      const msg = e.response?.data?.msg || e.message;
      // Refresh to pick up the persisted error state, then surface it.
      load();
      alert(`Run failed: ${msg}`);
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Archive this report?')) return;
    try {
      await axios.delete(`/api/reports/${id}`, headers);
      setReports((cur) => cur.filter((r) => r._id !== id));
    } catch (e) { console.error(e); }
  };

  const toggleTV = async (r) => {
    try {
      const res = await axios.put(`/api/reports/${r._id}`, { showOnTV: !r.showOnTV }, headers);
      setReports((cur) => cur.map((x) => (x._id === r._id ? res.data : x)));
    } catch (e) { console.error(e); }
  };

  const saved = (rep) => {
    setReports((cur) => {
      const exists = cur.some((r) => r._id === rep._id);
      return exists ? cur.map((r) => (r._id === rep._id ? rep : r)) : [...cur, rep];
    });
    setEditing(null);
  };

  const byCompany = COMPANIES.map((c) => ({ company: c, items: reports.filter((r) => r.company === c) }));
  const others = reports.filter((r) => !COMPANIES.includes(r.company));
  if (others.length) byCompany.push({ company: 'Other', items: others });

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tighter uppercase">Reports</h1>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">Live metrics, refreshed by Jim</p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs text-white bg-primary-blue hover:opacity-90 transition-all shadow-lg">
          + New Report
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-blue" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-4 opacity-20">📊</div>
          <p className="text-sm text-gray-400 font-bold uppercase tracking-widest">No reports yet. Create one to start.</p>
        </div>
      ) : (
        byCompany.filter((g) => g.items.length).map((group) => (
          <div key={group.company} className="mb-10">
            <h2 className="text-xl font-black text-gray-800 dark:text-gray-100 uppercase tracking-tight mb-4">{group.company}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {group.items.map((r) => (
                <ReportCard
                  key={r._id} report={r} busy={busyId === r._id}
                  onRun={() => runNow(r._id)} onEdit={() => setEditing(r)} onView={() => setViewing(r)}
                  onDelete={() => remove(r._id)} onToggleTV={() => toggleTV(r)} />
              ))}
            </div>
          </div>
        ))
      )}

      {editing && <ReportModal token={token} report={editing} onSaved={saved} onClose={() => setEditing(null)} />}
      {viewing && <DetailModal report={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

const ReportCard = ({ report, busy, onRun, onEdit, onView, onDelete, onToggleTV }) => {
  const r = report.result || {};
  const stats = r.stats || [];
  const hasDetail = !!r.detail || (r.charts || []).length > 0;
  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-2xl border border-gray-100 dark:border-gray-700 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <button onClick={onView} className="min-w-0 text-left group">
          <h3 className="text-sm font-black text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-blue transition-colors">{report.name}</h3>
          {r.headline && <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{r.headline}</p>}
        </button>
        {report.status === 'error' && (
          <span className="text-[9px] font-black uppercase tracking-widest text-red-500 bg-red-500/10 px-2 py-1 rounded-full flex-shrink-0">Error</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 my-4 flex-1">
        {stats.length === 0 && <p className="col-span-3 text-[11px] text-gray-400 italic py-4">No data yet. Run it.</p>}
        {stats.map((s, i) => (
          <div key={i} className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-2.5 text-center">
            <div className="text-base font-black tabular-nums text-gray-900 dark:text-gray-100 leading-tight">{s.value}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 truncate mt-0.5">{s.label}</div>
            {s.delta ? <div className={`text-[9px] font-black ${trendColor(s.trend)}`}>{s.delta}</div> : null}
          </div>
        ))}
      </div>

      {r.period && <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">{r.period}</p>}
      {report.lastError && <p className="text-[10px] text-red-400 mb-1 line-clamp-2">{report.lastError}</p>}

      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">
        <span>{report.frequency}</span>
        <span>updated {ago(report.lastRunAt)}</span>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onRun} disabled={busy}
          className="flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-white bg-primary-blue hover:opacity-90 disabled:opacity-40 transition-all">
          {busy ? 'Running…' : 'Run now'}
        </button>
        <button onClick={onView} disabled={!hasDetail} title={hasDetail ? 'View full report' : 'Run it to see the full report'}
          className="px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">View</button>
        <button onClick={onEdit} className="px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">Edit</button>
        <button onClick={onDelete} title="Archive" className="px-2 py-2 rounded-lg text-gray-300 hover:text-red-500 transition-colors">✕</button>
      </div>

      <label className="flex items-center gap-2 mt-3 cursor-pointer">
        <input type="checkbox" checked={!!report.showOnTV} onChange={onToggleTV} className="h-4 w-4 rounded accent-primary-blue" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">📺 Show on TV</span>
      </label>
    </div>
  );
};

const ReportModal = ({ token, report, onSaved, onClose }) => {
  const isNew = !report._id;
  const [form, setForm] = useState({
    name: report.name || '',
    company: report.company || COMPANIES[0],
    query: report.query || '',
    frequency: report.frequency || 'weekly',
    showOnTV: !!report.showOnTV,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.query.trim()) return;
    setSaving(true);
    try {
      const headers = { headers: { 'x-auth-token': token } };
      const res = isNew
        ? await axios.post('/api/reports', form, headers)
        : await axios.put(`/api/reports/${report._id}`, form, headers);
      onSaved(res.data);
    } catch (e2) { alert(e2.response?.data?.msg || e2.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-100">{isNew ? 'New Report' : 'Edit Report'}</h3>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Name</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus
              className="w-full mt-1 rounded-xl p-3 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 focus:ring-primary-blue focus:border-primary-blue" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Company</label>
              <select value={form.company} onChange={(e) => set('company', e.target.value)}
                className="w-full mt-1 rounded-xl p-3 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100">
                {COMPANIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Frequency</label>
              <select value={form.frequency} onChange={(e) => set('frequency', e.target.value)}
                className="w-full mt-1 rounded-xl p-3 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100">
                {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Query (plain English)</label>
              <div className="flex gap-1">
                {PRESETS.map((p) => (
                  <button key={p.label} type="button" onClick={() => set('query', p.query)}
                    className="text-[9px] font-bold uppercase tracking-widest text-primary-blue hover:underline">{p.label}</button>
                ))}
              </div>
            </div>
            <textarea value={form.query} onChange={(e) => set('query', e.target.value)} rows={4}
              placeholder="Describe what to pull, e.g. last 7 days of the Auto Refinance Google Ads campaign…"
              className="w-full mt-1 rounded-xl p-3 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 focus:ring-primary-blue focus:border-primary-blue" />
            <p className="text-[10px] text-gray-400 mt-1">Jim runs this through the vault skills and returns metric tiles. Tap a preset to start.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.showOnTV} onChange={(e) => set('showOnTV', e.target.checked)} className="h-4 w-4 rounded accent-primary-blue" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-300">📺 Show highlights on the TV display</span>
          </label>
        </form>
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-primary-blue hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailModal = ({ report, onClose }) => {
  const r = report.result || {};
  const stats = r.stats || [];
  const charts = r.charts || [];
  return (
    <div className="report-modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="report-modal-panel w-full max-w-3xl max-h-[88vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-gray-900 dark:text-gray-100 truncate">{report.name}</h3>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
              {report.company}{r.period ? ` · ${r.period}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 no-print">
            <button onClick={() => window.print()}
              className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-white bg-primary-blue hover:opacity-90 transition-all">
              Download PDF
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
          </div>
        </div>

        <div id="report-print-area" className="px-6 py-5 overflow-y-auto space-y-6">
          <div className="hidden print:block mb-2">
            <h1 className="text-2xl font-black text-gray-900">{report.name}</h1>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
              {report.company}{r.period ? ` · ${r.period}` : ''}
            </p>
          </div>
          {r.headline && <p className="text-sm font-bold text-gray-700 dark:text-gray-200">{r.headline}</p>}

          {stats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {stats.map((s, i) => (
                <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                  <div className="text-xl font-black tabular-nums text-gray-900 dark:text-gray-100 leading-tight">{s.value}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 truncate mt-0.5">{s.label}</div>
                  {s.delta ? <div className={`text-[10px] font-black ${trendColor(s.trend)}`}>{s.delta}</div> : null}
                </div>
              ))}
            </div>
          )}

          {charts.map((c, i) => (
            <div key={i} className="report-chart border border-gray-100 dark:border-gray-800 rounded-xl p-4">
              <div className="h-56">
                <MiniChart chart={c} labelClass="text-[10px]" valueClass="text-xs" />
              </div>
            </div>
          ))}

          {r.detail
            ? <MarkdownContent content={r.detail} />
            : <p className="text-sm text-gray-400 italic">No detailed write-up yet. Run the report to generate one.</p>}

          {report.lastError && <p className="text-xs text-red-400">{report.lastError}</p>}
        </div>
      </div>
    </div>
  );
};

export default Reports;
